import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaUserTie, FaUser, FaTractor, FaMapMarkedAlt, FaTint, FaWeightHanging, FaSeedling, FaChartLine } from 'react-icons/fa';
import { MdOutlineNoteAlt } from 'react-icons/md';
import { BsClipboard2DataFill } from 'react-icons/bs';
import { FiAlertCircle, FiInbox } from 'react-icons/fi';

import Wrapper from '../../components/Wrapper';
import KPICard, { KPICardSkeleton } from '../../components/Dashboard/KPICard';
import HealthDistributionChart from '../../components/Dashboard/HealthDistributionChart';
import { fetchPartnerDashboardForAdmin, fetchPartnerFiltersForAdmin } from '../../services/dashboardApi';
import '../../styles/dashboard.css';

const READONLY_METRIC_CONFIG = {
  fieldOfficers: { icon: <FaUserTie size={22} />, color: 'purple' },
  farmers: { icon: <FaUser size={22} />, color: 'green' },
  bookings: { icon: <MdOutlineNoteAlt size={22} />, color: 'amber' },
  soilReports: { icon: <BsClipboard2DataFill size={20} />, color: 'indigo' },
  waterReports: { icon: <FaTint size={20} />, color: 'sky' },
  farms: { icon: <FaTractor size={22} />, color: 'teal' },
  totalArea: { icon: <FaMapMarkedAlt size={22} />, color: 'cyan' },
  previousYield: { icon: <FaWeightHanging size={20} />, color: 'gray' },
  currentYield: { icon: <FaSeedling size={22} />, color: 'emerald' },
  yieldGrowth: { icon: <FaChartLine size={20} />, color: 'blue' },
};

const PartnerDashboardSummary = () => {
  const { admin_id } = useParams();
  const navigate = useNavigate();
  
  const [metrics, setMetrics] = useState([]);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  
  const [filters, setFilters] = useState({ state: '', city: '', crop: '' });
  const [filterOptions, setFilterOptions] = useState({ states: [], cities: [], crops: [] });

  const loadFilterOptions = useCallback(async () => {
    try {
      const data = await fetchPartnerFiltersForAdmin(admin_id);

      if (data.success) {
        setFilterOptions({
          states: data.states,
          cities: data.cities,
          crops: data.crops,
        });
      }
    } catch (err) {
      console.error("Failed to load filters", err);
    }
  }, [admin_id]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const loadMetrics = useCallback(async () => {
    setStatus('loading');
    setErrorMsg('');
    try {
      const data = await fetchPartnerDashboardForAdmin(admin_id, filters);
      if (data.success) {
        setMetrics(data.metrics);
        setStatus('success');
      } else {
        setErrorMsg(data.message || 'Failed to load metrics');
        setStatus('error');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message || 'Network error');
      setStatus('error');
    }
  }, [admin_id, filters]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const cardMetrics = metrics.filter(metric => metric.type !== 'chart');

  const chartMetrics = metrics.filter(metric => metric.type === 'chart');

  return (
    <Wrapper>
      <div className="dashboard-container">
        <div className="dashboard-header d-flex justify-content-between align-items-center">
          <div>
            <h1>Partner Summary Dashboard</h1>
            <p className="text-muted">Viewing read-only data for Admin ID: {admin_id}</p>
          </div>
          <button onClick={() => navigate(-1)} className="btn btn-outline-secondary btn-sm">
            &larr; Back to Partners
          </button>
        </div>

        <div className="dashboard-filters" style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <select name="state" value={filters.state} onChange={handleFilterChange} className="filter-input" style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}>
            <option value="">All States</option>
            {filterOptions.states.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          <select name="city" value={filters.city} onChange={handleFilterChange} className="filter-input" style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}>
            <option value="">All Cities</option>
            {filterOptions.cities.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>

          <select name="crop" value={filters.crop} onChange={handleFilterChange} className="filter-input" style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}>
            <option value="">All Crops</option>
            {filterOptions.crops.map((crop) => (
              <option key={crop} value={crop}>{crop}</option>
            ))}
          </select>
        </div>

        {status === 'loading' && (
          <div className="kpi-grid">
            {Array.from({ length: 6 }).map((_, i) => <KPICardSkeleton key={i} />)}
          </div>
        )}

        {status === 'error' && (
          <div className="dashboard-error">
            <FiAlertCircle />
            <h3>Unable to load dashboard</h3>
            <p>{errorMsg}</p>
            <button onClick={loadMetrics}>Retry</button>
          </div>
        )}

        {status === 'success' && metrics.length === 0 && (
          <div className="dashboard-empty">
            <FiInbox />
            <h3>No metrics available</h3>
          </div>
        )}

        {status === 'success' && metrics.length > 0 && (
        <>
            {/* KPI Cards */}
            <div className="kpi-grid">
            {cardMetrics.map((metric) => {
                const config = READONLY_METRIC_CONFIG[metric.key] || {};

                return (
                <KPICard
                    key={metric.key}
                    title={metric.title}
                    count={metric.count}
                    timeFilter={metric.timeFilter}
                    trend={metric.trend}
                    icon={config.icon}
                    color={config.color}
                />
                );
            })}
            </div>

            {/* Charts */}
            {chartMetrics.length > 0 && (
            <div className="chart-grid">
                {chartMetrics.map((metric) => (
                <HealthDistributionChart
                    key={metric.key}
                    title={metric.title}
                    data={metric.data}
                />
                ))}
            </div>
            )}
        </>
        )}
      </div>
    </Wrapper>
  );
};

export default PartnerDashboardSummary;